import React, { ReactNode, useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';
import Button from '@atlaskit/button';
import styled from '@emotion/styled';

import { OidcDisplay } from '../types';
import { doLogout, manageSession } from '../actions/session-actions';
import { useNavigateToLoginPrompt } from '../utilities/hooks/history';
import {
  EventAttributes,
  useAnalyticsClient,
  usePageViewedEvent,
} from '../utilities/analytics/analytics-web-client';
import messages from './RemoveAccountPage.messages';
import Modal, { ModalBody, ModalFooter, ModalHeader, ModalTitle } from '@atlaskit/modal-dialog';
import Account, {
  AccountContainer,
  AvatarContainer,
  Paragraph,
  RowButtonContainer,
  TextContainer,
} from '../components/Account';
import { Session, sessionsSelector } from '../selectors/sessions';
import Accounts from '../components/Accounts';
import RemoveIcon from '@atlaskit/icon/glyph/sign-out';
import ButtonLink from '../components/ButtonLink';
import WhiteboxPage from '../components/WhiteboxPage';
import { isOidcDisplayMode } from '../utilities/oidc/oidc';
import { useLoginHint } from '../utilities/hooks/login-hint';
import { useAppDispatch, useAppSelector } from '../stores/redux.store';
import { useNavigateSoftOrHard } from '../utilities/hooks/useNavigateSoftOrHard';
import { usePageStateLoader, fetchPageState } from '../utilities/hooks/useGlobalPageState';
import { AidFrontendFeatureGate } from '../providers/FeatureGates';
import { checkFeatureGateOnceInitialized } from '../utilities/feature-flags/feature-gate';

import Heading from '@atlaskit/heading';
export interface State {
  modalDialog: ReactNode | null;
}

const pageId = 'forgetAccountFromBrowserScreen';

export async function removeAccountLoader({ request }: { request: Request }) {
  if (!(await checkFeatureGateOnceInitialized(AidFrontendFeatureGate.USE_STATE_DATA_LOADERS))) {
    return null;
  }

  return await fetchPageState('/rest/login/remove-account/state', request, {
    capabilities: ['capability-group--manage-profile', 'capability-name--remove-account'],
  });
}

export const RemoveAccountPage = () => {
  usePageStateLoader();

  const { formatMessage } = useIntl();
  const analyticsClient = useAnalyticsClient();
  const navigate = useNavigateSoftOrHard();
  const loginHint = useLoginHint();
  const { search } = useLocation();
  const navigateToLoginPrompt = useNavigateToLoginPrompt();

  const [modalDialog, setModalDialog] = useState<ReactNode>(null);

  const sessions = useAppSelector(state => sessionsSelector(state, search, loginHint));
  const oidcContext = useAppSelector(state => state.microbranding.oidcContext);
  const isLoading = useAppSelector(state => state.selectAccount.isLoading);

  const dispatch = useAppDispatch();

  const eventAttributes = useMemo(() => ({ accountCount: sessions.length }), [sessions.length]);

  usePageViewedEvent(pageId, eventAttributes);

  const renderFooter = () => {
    if (isOidcDisplayMode(oidcContext, OidcDisplay.ManageSessions)) {
      return null;
    }

    return (
      <ButtonLink
        id="navigate-to-select-account"
        onClick={() => {
          analyticsClient.buttonClickedEvent(pageId, 'dismissAccountSwitcher', eventAttributes);
          navigate({ pathname: '/login/select-account', search });
        }}>
        <FormattedMessage
          id="remove.account.footer.navigate.to.select.account"
          defaultMessage="Done"
        />
      </ButtonLink>
    );
  };

  const handleSessionClick = useCallback(
    (
      session: Session,
      eventAttributes: EventAttributes
    ): ((_: React.MouseEvent<HTMLElement>) => void) => {
      return () => {
        const handleDissociate = () => {
          analyticsClient.buttonClickedEvent(pageId, 'confirmForgetAccount', eventAttributes);

          dispatch(
            manageSession({ sessionId: session.sessionId, command: 'dissociate' }, navigate, {
              continue: window.location.href,
            })
          );
        };

        const handleCloseModalDialog = () => {
          analyticsClient.buttonClickedEvent(pageId, 'cancelForgetAccount', eventAttributes);
          setModalDialog(null);
        };

        const modalDialogElement = (
          <Modal width="small" onClose={handleCloseModalDialog}>
            <ModalHeader>
              <ModalTitle>
                <FormattedMessage
                  id="remove.account.dialog.header"
                  defaultMessage="Remove account"
                />
              </ModalTitle>
            </ModalHeader>
            <ModalBody>
              <FormattedMessage
                id="remove.account.dialog.body"
                defaultMessage="You can always add the account for <strong>{email}</strong> back to this browser when you're ready to use it again."
                values={{
                  strong: chunks => <strong>{chunks}</strong>,
                  email: session.email,
                }}
              />
            </ModalBody>
            <ModalFooter>
              <Button appearance="subtle" onClick={handleCloseModalDialog}>
                <FormattedMessage {...messages.removeAccountDialogClose} />
              </Button>
              <Button appearance="primary" onClick={handleDissociate} autoFocus>
                <FormattedMessage {...messages.removeAccountDialogConfirm} />
              </Button>
            </ModalFooter>
          </Modal>
        );

        analyticsClient.buttonClickedEvent(pageId, 'forgetAccountFromBrowser', eventAttributes);
        setModalDialog(modalDialogElement);
      };
    },
    [analyticsClient, dispatch, navigate]
  );

  const handleLogoutClick = useCallback((): void => {
    const handleCloseModalDialog = () => {
      analyticsClient.buttonClickedEvent(pageId, 'cancelForgetAllAccounts', eventAttributes);
      setModalDialog(null);
    };

    const modalDialogElement = (
      <Modal width="small" onClose={handleCloseModalDialog}>
        <ModalHeader>
          <ModalTitle>
            <FormattedMessage
              id="remove.account.logout.dialog.header"
              defaultMessage="Remove all accounts"
            />
          </ModalTitle>
        </ModalHeader>
        <ModalBody>
          <FormattedMessage
            id="remove.account.logout.dialog.body"
            defaultMessage="You can always add the accounts back to this browser when you're ready to use any of them again."
          />
        </ModalBody>
        <ModalFooter>
          <Button appearance="subtle" onClick={handleCloseModalDialog}>
            <FormattedMessage {...messages.removeAccountDialogClose} />
          </Button>
          <Button
            appearance="primary"
            onClick={async () => {
              await analyticsClient.buttonClickedEvent(
                pageId,
                'confirmForgetAllAccounts',
                eventAttributes
              );

              const continueUrl = isOidcDisplayMode(oidcContext, OidcDisplay.ManageSessions)
                ? window.location.href
                : undefined;

              await dispatch(doLogout(continueUrl));
            }}
            autoFocus>
            <FormattedMessage {...messages.removeAccountDialogConfirm} />
          </Button>
        </ModalFooter>
      </Modal>
    );

    analyticsClient.buttonClickedEvent(pageId, 'forgetAllAccounts', eventAttributes);

    setModalDialog(modalDialogElement);
  }, [analyticsClient, dispatch, eventAttributes, oidcContext]);

  const renderAccounts = () => {
    const accounts = sessions.map((session, index) => {
      const { accountId, displayName, email } = session;
      const handleOnClick = handleSessionClick(session, {
        ...eventAttributes,
        accountIndex: index,
      });

      return (
        <Account
          id={`account-${accountId}`}
          key={`account-${accountId}`}
          onClick={handleOnClick}
          avatarUrl={session.avatarUrl}
          displayName={displayName}
          email={email}
          icon={<RemoveIcon label={formatMessage(messages.removeAccountIconLabel)} size="small" />}
        />
      );
    });

    accounts.push(
      <AccountContainer
        id="redirect-to-logout"
        key="redirect-to-logout"
        onClick={handleLogoutClick}>
        <RowButtonContainer>
          <AvatarContainer>
            <RemoveIcon label={formatMessage(messages.removeAccountIconLabel)} />
          </AvatarContainer>
          <TextContainer>
            <Paragraph id="remove-all">
              <FormattedMessage id="remove.account.all" defaultMessage="Remove all accounts" />
            </Paragraph>
          </TextContainer>
        </RowButtonContainer>
      </AccountContainer>
    );
    return accounts;
  };

  if (sessions.length > 0) {
    return (
      <WhiteboxPage
        pageId={pageId}
        header={
          <Heading as="h1" size="small">
            <FormattedMessage
              id="remove.account.title"
              defaultMessage="Remove accounts from this browser"
            />
          </Heading>
        }
        footer
        whiteboxFooter={renderFooter()}
        isLoading={isLoading}>
        {modalDialog}
        <Accounts>{renderAccounts()}</Accounts>
      </WhiteboxPage>
    );
  } else if (isOidcDisplayMode(oidcContext, OidcDisplay.ManageSessions)) {
    return (
      <WhiteboxPage
        pageId={pageId}
        header={
          <Heading as="h1" size="small">
            <FormattedMessage
              id="manage.sessions.no.sessions.header"
              defaultMessage="You're logged out of all accounts on this browser"
            />
          </Heading>
        }>
        <CenterText>
          <FormattedMessage
            tagName="p"
            id="manage.sessions.no.sessions.body"
            defaultMessage="Close this page to return to the app."
          />
        </CenterText>
      </WhiteboxPage>
    );
  } else {
    navigateToLoginPrompt();
    return null;
  }
};

const CenterText = styled.div`
  text-align: center;
`;

export default RemoveAccountPage;
